import { useContext, useState } from 'react';

import { fak } from '@awesome.me/kit-2c0e92f331/icons';
import { datadogRum } from '@datadog/browser-rum';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Stack, Typography } from '@mui/material';
import { RadioGroupOptions, RadioGroupVariants } from '@sage/ui-components';

import DrawerBody from './DrawerBody';
import DrawerFooter from './DrawerFooter';
import { DrawerComponentProps } from './DrawerManager';
import DrawerTitle from './DrawerTitle';
import { AnalyticsContext } from 'src/AnalyticsContext';
import { CheckInReasons, CheckInStatuses } from 'src/constants/CheckIn';
import { CheckIn, Unit, useUpdateCheckInMutation } from 'src/store/sageAdminApi';
import { snackActions } from 'src/utils/snackBarTools';

const DrawerName = 'Unit Check-In Drawer';

const CheckInOptions = [
    {
        label: 'Seen in-person',
        value: CheckInReasons.SeenInPerson,
        icon: fak.faVisible as IconDefinition,
    },
    {
        label: 'Called resident',
        value: CheckInReasons.CalledResident,
        icon: fak.faPhone as IconDefinition,
    },
    {
        label: 'Team member verified',
        value: CheckInReasons.TeamMemberVerified,
        icon: fak.faDuo as IconDefinition,
    },
];

export interface UnitCheckInDrawerProps extends DrawerComponentProps {
    unit: Unit & { checkIn?: CheckIn };
    onNext: () => void;
}

export default function UnitCheckInDrawer(props: UnitCheckInDrawerProps) {
    const { hasPreviousDrawer, onClose, onPrevious, onNext, unit } = props;

    const { trackEvent } = useContext(AnalyticsContext);

    const [selectedReason, setSelectedReason] = useState('');

    const [updateCheckIn, { isLoading: isUpdatingCheckIn }] = useUpdateCheckInMutation();

    const handleCheckIn = async () => {
        const checkInReason = selectedReason;
        const buttonText =
            CheckInOptions.find(({ value }) => value === checkInReason)
                ?.label.toLocaleLowerCase()
                .replaceAll(' ', '-') || checkInReason;
        trackEvent('Check-in Reason Selected', { checkInReason, buttonText, context: DrawerName });

        try {
            await updateCheckIn({
                checkInId: unit?.checkIn?.id!,
                updateCheckInRequest: {
                    status: CheckInStatuses.Complete,
                    checkInReason,
                },
            }).unwrap();

            snackActions.success(`Check-in updated for unit ${unit.displayName}`);
            onNext();
        } catch (error) {
            datadogRum.addError(error, {
                message: 'Error updating check in',
                unitId: unit?.id,
                checkInReason,
            });
            snackActions.error('Error updating check in, please try again');
        }
    };

    return (
        <>
            <DrawerTitle title="Select option to check-in" />
            <DrawerBody>
                <RadioGroupOptions
                    radioGroupProps={{ 'aria-label': 'check-in reason' }}
                    variant={RadioGroupVariants.Outlined}
                    selectedOption={selectedReason}
                    onSelectOption={setSelectedReason}
                    options={CheckInOptions.map(({ label, value, icon }) => ({
                        value,
                        label: (
                            <Stack direction="row" sx={{ alignItems: 'center', gap: 1.5 }}>
                                <FontAwesomeIcon icon={icon} size="xl" />
                                <Typography variant="body1" sx={{ fontWeight: 'bolder' }}>
                                    {label}
                                </Typography>
                            </Stack>
                        ),
                    }))}
                />
            </DrawerBody>
            <DrawerFooter sx={{ gap: 2 }}>
                <Button
                    color="primary"
                    variant="contained"
                    disableElevation
                    fullWidth
                    disabled={!selectedReason}
                    loading={isUpdatingCheckIn}
                    startIcon={<FontAwesomeIcon icon={fak.faCalendarCheck as IconDefinition} />}
                    onClick={handleCheckIn}
                >
                    Check-in Unit
                </Button>
                <Button
                    color="primary"
                    disableElevation
                    fullWidth
                    disabled={isUpdatingCheckIn}
                    onClick={hasPreviousDrawer ? () => onPrevious(false) : onClose}
                >
                    {hasPreviousDrawer ? 'Go Back' : 'Cancel'}
                </Button>
            </DrawerFooter>
        </>
    );
}
